import Modal from './Modal';

/**
 * TierLimitModal — the canonical "you've hit a plan limit" wall.
 *
 * The conversion-lever sibling of BlockModal: where BlockModal explains a
 * data-integrity guardrail (nothing to buy), this fronts a tier/feature gate and
 * carries the upgrade path. Per the tier-surfaces-are-conversion-levers rule it
 * always shows (a) what's blocked, (b) what unlocks it, and (c) a one-click route
 * to billing. Copy comes from tierLimits.ts; this is just the chrome + CTA.
 *
 * Used through the global useBlockDialog() hook (showUpgrade) so any surface gets
 * the same wall without wiring its own modal state. See AVM-019.
 */
interface TierLimitModalProps {
  open: boolean;
  title: string;
  /** What the user tried that's gated. */
  what: string;
  /** Which plan unlocks it. */
  unlocks: string;
  /** Optional specifics (e.g. "Max 3 projects on your current plan"). */
  detail?: string;
  /** Invoked when the user clicks the upgrade CTA (navigates to billing). */
  onUpgrade: () => void;
  onClose: () => void;
}

export default function TierLimitModal({
  open,
  title,
  what,
  unlocks,
  detail,
  onUpgrade,
  onClose,
}: TierLimitModalProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      branded
      title={title}
      subtitle="Unlock more with a higher plan"
      footer={
        <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            className="inline-flex items-center justify-center px-4 py-2 rounded-md border border-line text-ink-muted text-sm font-semibold hover:bg-wash transition-colors"
          >
            Not now
          </button>
          <button
            onClick={onUpgrade}
            className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-brand-primary text-white text-sm font-semibold hover:bg-brand-primary-dark transition-colors"
          >
            View plans
          </button>
        </div>
      }
    >
      <div className="flex gap-3">
        <div className="shrink-0 mt-0.5 flex items-center justify-center w-9 h-9 rounded-full bg-brand-primary/10 text-brand-primary dark:text-brand-accent text-lg">
          ★
        </div>
        <div className="text-sm text-ink-muted space-y-2">
          <p>{what}</p>
          {detail && <p className="font-semibold text-ink">{detail}</p>}
          <p className="text-ink-faint">{unlocks}</p>
        </div>
      </div>
    </Modal>
  );
}
